<template>
  <div class="seo-page">
    <section class="seo-hero">
      <span class="seo-badge">Steam Wasted Hours</span>
      <h1>How Many Hours Have You "Wasted" on Steam?</h1>
      <p>
        Everyone jokes about hours "wasted" on Steam — steamplayti.me turns the joke into an actual number.
        See your total playtime broken down by game, day, and streak, and decide for yourself if it was wasted
        or well spent.
      </p>
      <div class="seo-cta-group">
        <router-link class="seo-primary-btn" to="/">Calculate my wasted hours</router-link>
        <a class="seo-secondary-link" href="#wasted-breakdown">See the breakdown →</a>
      </div>
      <ul class="seo-highlight-list">
        <li>
          <span class="seo-highlight-icon">💀</span>
          <div>Get a real total across every game — not just the one Steam highlights on your profile.</div>
        </li>
        <li>
          <span class="seo-highlight-icon">📊</span>
          <div>See which single game ate the most hours, and which days were the worst offenders.</div>
        </li>
        <li>
          <span class="seo-highlight-icon">🔗</span>
          <div>Share the number with friends — or use it to finally justify that backlog purchase.</div>
        </li>
      </ul>
    </section>

    <section class="seo-section" id="wasted-breakdown">
      <h2>Your Hours, Broken Down</h2>
      <p>
        "Wasted" is relative — a total is only funny (or alarming) once you can actually see it. steamplayti.me
        turns your raw Steam data into a number you can point to.
      </p>
      <div class="seo-grid">
        <div class="seo-card">
          <h3>Lifetime Total</h3>
          <p>One number across your whole library, pulled straight from Steam — no manual adding up required.</p>
        </div>
        <div class="seo-card">
          <h3>Worst Offender</h3>
          <p>Instantly see the single game responsible for the biggest chunk of your total hours.</p>
        </div>
        <div class="seo-card">
          <h3>Daily Pattern</h3>
          <p>Find the days and streaks where the hours really added up — and the quiet stretches in between.</p>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>Not Actually Wasted</h2>
      <p>
        We built steamplayti.me on the idea that seeing the number is more useful than avoiding it. Whether you
        want to cut back, brag about a completionist run, or just satisfy curiosity, the data is yours either way.
      </p>
      <div class="seo-stats">
        <div class="seo-stat">
          <div class="label">Coverage</div>
          <div class="value">Whole library</div>
        </div>
        <div class="seo-stat">
          <div class="label">Update speed</div>
          <div class="value">Automatic</div>
        </div>
        <div class="seo-stat">
          <div class="label">Judgment</div>
          <div class="value">None included</div>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>Steam Wasted Hours FAQ</h2>
      <div class="seo-faq">
        <div class="seo-faq-item">
          <h3>How do I see how many hours I've wasted on Steam?</h3>
          <p>Search your Steam ID on steamplayti.me and your dashboard shows total hours across your whole library, plus a per-game and per-day breakdown.</p>
        </div>
        <div class="seo-faq-item">
          <h3>Does this include hours from games I no longer own or play?</h3>
          <p>Yes, as long as the game is still on your Steam account and its playtime isn't set to private, it counts toward your total.</p>
        </div>
        <div class="seo-faq-item">
          <h3>Can I see which game "wasted" the most time?</h3>
          <p>Your dashboard ranks games by total playtime, so the biggest time sink is always at the top.</p>
        </div>
        <div class="seo-faq-item">
          <h3>Is a high number actually a bad thing?</h3>
          <p>Not necessarily — that's up to you. We just show the data; whether it counts as "wasted" or "well spent" is your call.</p>
        </div>
      </div>
    </section>
    <SeoRelatedTools current-slug="steam-wasted-hours" />
  </div>
</template>

<script>
import { applyMeta, pageStructuredData } from '../utils/meta';
import SeoRelatedTools from '../components/SeoRelatedTools.vue';

const META_DESCRIPTION = 'Find out exactly how many hours you\'ve "wasted" on Steam. steamplayti.me totals your playtime across your whole library, broken down by game and day.';

const META = {
  title: 'How Many Hours Have You Wasted on Steam? | steamplayti.me',
  description: META_DESCRIPTION,
  keywords: 'steam wasted hours, time wasted on steam, steam wasted time, how much time wasted on steam, steam total hours',
  canonical: 'https://steamplayti.me/page/steam-wasted-hours/',
  ogTitle: 'How Many Hours Have You Wasted on Steam?',
  ogDescription: 'See your real total Steam playtime across every game, broken down by day and title.',
  ogUrl: 'https://steamplayti.me/page/steam-wasted-hours/',
  twitterTitle: 'How Many Hours Have You Wasted on Steam?',
  twitterDescription: 'steamplayti.me totals up every hour you\'ve spent on Steam — see the real number.',
  structuredData: pageStructuredData({
    name: 'Steam Wasted Hours',
    description: META_DESCRIPTION,
    canonical: 'https://steamplayti.me/page/steam-wasted-hours/',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Tools & Blog', path: '/blog/' },
      { name: 'Steam Wasted Hours', path: 'https://steamplayti.me/page/steam-wasted-hours/' },
    ],
  }),
};

export default {
  name: 'SteamWastedHoursPage',
  components: { SeoRelatedTools },
  mounted() {
    applyMeta(META);
  },
  beforeUnmount() {
    applyMeta();
  },
};
</script>

<style src="../styles/seo-page.css"></style>
